import { GetGeneralInformationResponse } from "@1games-ru/region-shared/dist/backend/requests/faction";
import { cloneDeep } from "lodash";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Settings2Icon } from "@/shared/icons";
import { Button, Switcher } from "@/shared/ui";
import { FactionEnum } from "@/domains/faction/constants/factionEnum";
import { useChangePermissions } from "@/domains/faction/hooks/useChangePermissions";
import { QUERY_KEY_FACTION_GENERAL_INFO } from "@/domains/faction/hooks/useGetInfo";
import FactionConfirmModal from "@/domains/faction/components/FactionConfirmModal";
import FactionListAside from "@/domains/faction/components/FactionListAside";
import FactionSettingsItem from "@/domains/faction/components/FactionSettingsItem";
import FactionTabsRow from "@/domains/faction/components/FactionTabsRow";

type Props = {
  faction: FactionEnum;
};

export default function FactionSettingsContainer({ faction }: Props) {
  const { t } = useTranslation("faction");
  const queryClient = useQueryClient();

  const factionInfo = queryClient.getQueryData([
    QUERY_KEY_FACTION_GENERAL_INFO,
  ]) as GetGeneralInformationResponse;

  const ranksArray = useMemo(() => {
    if (!factionInfo?.ranks) return [];
    return cloneDeep(factionInfo?.ranks).reverse();
  }, [factionInfo?.ranks]);

  const [activeRank, setActiveRank] = useState(0);
  const [rankAccesses, setRankAccesses] = useState(ranksArray[0]?.accesses);
  const [isResetModal, setIsResetModal] = useState(false);
  const [isSaveModal, setIsSaveModal] = useState(false);

  const { mutate: changePermissions, isPending: isChangePermissionsPending } =
    useChangePermissions();

  const onChangePermissions = useCallback(() => {
    changePermissions({
      rankId: ranksArray[activeRank].id,
      name: ranksArray[activeRank].name,
      accesses: rankAccesses,
      cb: () => setIsSaveModal(false),
    });
  }, [activeRank, changePermissions, rankAccesses, ranksArray]);

  const changeRankAccesses = useCallback(
    (idx: number) => {
      setRankAccesses(ranksArray[idx].accesses);
    },
    [ranksArray],
  );

  const resetToDefault = useCallback(() => {
    setRankAccesses({ ...ranksArray[activeRank].accesses });
    setIsResetModal(false);
  }, [activeRank, ranksArray]);

  const changeSwitcher = useCallback((key, value) => {
    setRankAccesses((prev) => ({
      ...prev,
      [key]: !value,
    }));
  }, []);

  return (
    <div className="flex flex-col h-full p-6">
      <FactionTabsRow title={t("faction.titles.settings")} Icon={Settings2Icon} isNoTabs />
      <div className="flex h-full min-h-0">
        <div className="flex flex-col h-full min-h-0">
          <p className="mb-3.5 text-12 font-bold text-[#B8B8B8] uppercase">
            {t("faction.ranksList")}
          </p>
          <div className="h-full min-h-0">
            <FactionListAside
              faction={faction}
              activeRank={activeRank}
              setActiveRank={setActiveRank}
              setRankAccesses={changeRankAccesses}
              list={ranksArray}
            />
          </div>
        </div>
        <div className="relative flex flex-col size-full min-h-0">
          <p className="mb-3.5 text-12 font-bold text-[#B8B8B8] uppercase">
            {t("faction.ranksNames")}
          </p>
          <div className="h-full min-h-0">
            <div className="relative flex flex-col h-full min-h-0">
              <div className="absolute top-0 left-0 bottom-0 w-[calc(100%+0.375rem)] pb-5 pr-1.5 overflow-y-auto overflow-x-clip [--scrollbar-track-bg:#181818] [--scrollbar-thumb-bg:#434242] [scrollbar-gutter:stable]">
                <FactionSettingsItem text={t("faction.rankName")} isTop>
                  <div className="flex justify-center items-center py-3 px-5 text-14 font-medium bg-dark-800 rounded-[0.4375rem]">
                    {ranksArray[activeRank]?.name}
                  </div>
                </FactionSettingsItem>
                {rankAccesses &&
                  Object.entries(rankAccesses).map(([key, value]) => (
                    <FactionSettingsItem key={key} text={t(`faction.${key}`)}>
                      <Switcher on={value} withBorder onChange={() => changeSwitcher(key, value)} />
                    </FactionSettingsItem>
                  ))}
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="absolute z-2 bottom-0 left-0 w-full h-7 bg-gradient-to-t from-dark-900 to-transparent pointer-events-none" />
            <div className="absolute z-2 bottom-0 left-0 w-full h-5 bg-gradient-to-t from-dark-900 to-transparent pointer-events-none" />
          </div>
          <div className="flex justify-between pt-3.5">
            <Button
              variant="solid"
              className="w-[19.25rem] h-12 bg-white/3 hover:bg-white/6"
              onClick={() => setIsResetModal(true)}
            >
              {t("faction.resetToDefault")}
            </Button>
            <Button className="w-[19.25rem] h-12" onClick={() => setIsSaveModal(true)}>
              {t("faction.saveChanges")}
            </Button>
          </div>
        </div>
      </div>
      {isResetModal && (
        <FactionConfirmModal
          title={t("faction.modals.resetToDefault.title")}
          description={t("faction.modals.resetToDefault.description")}
          onClose={() => setIsResetModal(false)}
          onConfirm={resetToDefault}
          faction={faction}
        />
      )}
      {isSaveModal && (
        <FactionConfirmModal
          title={t("faction.modals.saveChanges.title")}
          description={t("faction.modals.saveChanges.description")}
          onClose={() => setIsSaveModal(false)}
          onConfirm={onChangePermissions}
          isConfirming={isChangePermissionsPending}
          faction={faction}
        />
      )}
    </div>
  );
}
